import React, { useEffect } from 'react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="privacy-policy-page">
      <Header />
      
      <PageHero
        title="Privacy Policy"
        subtitle="How we collect, use, and protect your information"
        pageKey="privacy-policy"
      />

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 rounded-lg p-8 shadow-sm">
            <div className="mb-8 pb-6 border-b border-gray-200">
              <p className="text-sm text-gray-600">
                <span className="font-semibold">Effective Date:</span> {currentDate}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-semibold">Last Updated:</span> {currentDate}
              </p>
            </div>

            <div className="prose prose-gray max-w-none space-y-8">
              {/* Introduction */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Introduction</h2>
                <p className="text-gray-700 leading-relaxed text-justify">
                  BCS ("Company," "we," "our," or "us") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage with our services. By accessing or using our website, you consent to the practices described in this Privacy Policy.
                </p>
              </div>

              {/* Information We Collect */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
                <p className="text-gray-700 leading-relaxed mb-4">We may collect the following types of information:</p>
                
                <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Personal Information</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Name, job title, and company name</li>
                  <li>Email address and phone number</li>
                  <li>Mailing address and business location</li>
                  <li>Information provided through contact forms, quote requests, or service inquiries</li>
                  <li>Account credentials if you register for our customer portal</li>
                </ul>

                <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Automatically Collected Information</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>IP address and browser type</li>
                  <li>Device information and operating system</li>
                  <li>Pages visited, time spent on pages, and navigation paths</li>
                  <li>Referring website addresses</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>

              {/* How We Use Your Information */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">We use the information we collect for the following purposes:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>To respond to your inquiries, quote requests, and service questions</li>
                  <li>To provide, maintain, and improve our services</li>
                  <li>To process transactions and send related information</li>
                  <li>To send administrative communications, updates, and security alerts</li>
                  <li>To send marketing communications (with your consent where required)</li>
                  <li>To analyze website usage and improve user experience</li>
                  <li>To comply with legal obligations and protect our rights</li>
                </ul>
              </div>

              {/* Information Sharing */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Information Sharing and Disclosure</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Service Providers:</strong> With trusted third-party vendors who assist us in operating our website, conducting business, or servicing you, provided they agree to keep your information confidential</li>
                  <li><strong>Business Partners:</strong> With manufacturers, distributors, or partners when necessary to fulfill your service requests</li>
                  <li><strong>Legal Requirements:</strong> When required by law, subpoena, court order, or governmental regulation</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of company assets</li>
                  <li><strong>Protection of Rights:</strong> To protect the rights, property, or safety of BCS, our customers, or others</li>
                </ul>
              </div>

              {/* Cookies */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Cookies and Tracking Technologies</h2>
                <p className="text-gray-700 leading-relaxed text-justify">
                  Our website may use cookies, web beacons, and similar tracking technologies to enhance your browsing experience, analyze site traffic, and understand user behavior. You can control cookie preferences through your browser settings. Please note that disabling cookies may affect certain website functionality.
                </p>
              </div>

              {/* Data Security */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Security</h2>
                <p className="text-gray-700 leading-relaxed text-justify">
                  We implement reasonable administrative, technical, and physical security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                </p>
              </div>

              {/* Data Retention */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Retention</h2>
                <p className="text-gray-700 leading-relaxed text-justify">
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When personal information is no longer needed, we will securely delete or anonymize it.
                </p>
              </div>

              {/* Your Rights */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Rights and Choices</h2>
                <p className="text-gray-700 leading-relaxed mb-4">Depending on your location, you may have certain rights regarding your personal information:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal obligations</li>
                  <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
                  <li><strong>Data Portability:</strong> Request transfer of your data in a portable format</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  To exercise any of these rights, please contact us using the information provided below.
                </p>
              </div>

              {/* California Privacy Rights */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">California Privacy Rights</h2>
                <p className="text-gray-700 leading-relaxed text-justify">
                  If you are a California resident, you may have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect, the right to request deletion, and the right to opt-out of the sale of personal information. BCS does not sell personal information to third parties.
                </p>
              </div>

              {/* Third-Party Links */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Third-Party Links</h2>
                <p className="text-gray-700 leading-relaxed text-justify">
                  Our website may contain links to third-party websites or services that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services. We encourage you to review the privacy policies of any third-party sites you visit.
                </p>
              </div>

              {/* Children's Privacy */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
                <p className="text-gray-700 leading-relaxed text-justify">
                  Our website and services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately so we can delete such information.
                </p>
              </div>

              {/* Changes to Privacy Policy */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
                <p className="text-gray-700 leading-relaxed text-justify">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the updated policy on this page with a new "Last Updated" date. Your continued use of our website after any changes constitutes your acceptance of the revised Privacy Policy.
                </p>
              </div>

              {/* Contact Us */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-gray-700"><strong>BCS</strong></p>
                  <p className="text-gray-700">Phone: (949) 371-9948</p>
                  <p className="text-gray-700">Email: info@bcs-us.com</p>
                </div>
              </div>

              {/* Governing Law */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Governing Law</h2>
                <p className="text-gray-700 leading-relaxed text-justify">
                  This Privacy Policy shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
